'use client';

import { c as _c } from "react/compiler-runtime";
import { CROSSPOST_STATUS_TEXT, unsupportedReason } from '@shared/all/crosspost';
import type { CrossPostPlatform } from '@shared/all/validators';
import type { LucideIcon } from 'lucide-react';
import { AlertCircle, CalendarClock, Check, ChevronRight, ExternalLink, Facebook, Instagram, Linkedin, Loader2, MessageCircle, Music, RefreshCw, RotateCcw, Trash2, Twitter, Youtube } from 'lucide-react';
type LatestMetrics = {
  views: number;
  likes: number;
  shares: number;
  comments: number;
};
type CrossPostRecord = {
  id: number;
  platform: CrossPostPlatform;
  platformPostId: string | null;
  platformPostUrl: string | null;
  status: string;
  error: string | null;
  scheduledFor: Date | null;
  postedAt: Date | null;
  metrics: LatestMetrics[];
};
type Props = {
  platform: CrossPostPlatform;
  compatible: boolean;
  connected: boolean;
  contentType: string;
  costTrons: number;
  selected: boolean;
  onToggle: (platform: CrossPostPlatform) => void;
  crossPost?: CrossPostRecord;
  onRetry?: (crossPostId: number) => void;
  onRefreshMetrics?: (crossPostId: number) => void;
  onCancelScheduled?: (crossPostId: number) => void;
  validationIssues?: string[];
  // Non-blocking heads-up, e.g. long text that only X Premium can post.
  warning?: string | null;
};
const PLATFORM_CONFIG: Record<CrossPostPlatform, {
  label: string;
  icon: LucideIcon;
  color: string;
  bg: string;
}> = {
  TIKTOK: {
    label: 'TikTok',
    icon: Music,
    color: 'text-[#ff0050]',
    bg: 'bg-[#ff0050]/10'
  },
  YOUTUBE: {
    label: 'YouTube',
    icon: Youtube,
    color: 'text-[#FF0000]',
    bg: 'bg-[#FF0000]/10'
  },
  INSTAGRAM: {
    label: 'Instagram',
    icon: Instagram,
    color: 'text-[#E4405F]',
    bg: 'bg-[#E4405F]/10'
  },
  TWITTER: {
    label: 'X / Twitter',
    icon: Twitter,
    color: 'text-[#1DA1F2]',
    bg: 'bg-[#1DA1F2]/10'
  },
  LINKEDIN: {
    label: 'LinkedIn',
    icon: Linkedin,
    color: 'text-[#0A66C2]',
    bg: 'bg-[#0A66C2]/10'
  },
  FACEBOOK: {
    label: 'Facebook',
    icon: Facebook,
    color: 'text-[#1877F2]',
    bg: 'bg-[#1877F2]/10'
  },
  REDDIT: {
    label: 'Reddit',
    icon: MessageCircle,
    color: 'text-[#FF4500]',
    bg: 'bg-[#FF4500]/10'
  }
};
function getPlatformPostUrl(platform: CrossPostPlatform, postId: string): string {
  switch (platform) {
    case 'TIKTOK':
      return `https://www.tiktok.com/@/video/${postId}`;
    case 'YOUTUBE':
      return `https://www.youtube.com/watch?v=${postId}`;
    case 'INSTAGRAM':
      return `https://www.instagram.com/p/${postId}`;
    case 'FACEBOOK':
      return `https://www.facebook.com/${postId}`;
    case 'TWITTER':
      return `https://x.com/i/status/${postId}`;
    case 'LINKEDIN':
      return `https://www.linkedin.com/feed/update/${postId}`;
    case 'REDDIT':
      return `https://www.reddit.com/comments/${postId}`;
    default:
      return '#';
  }
}
function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// Scheduled times are minutes-precision by construction, so a short local
// timestamp is all the card needs.
function formatScheduledFor(date: Date): string {
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
function StatusIndicator(t0) {
  const $ = _c(12);
  const {
    status,
    error,
    scheduledFor
  } = t0;
  switch (status) {
    case "SCHEDULED":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <CalendarClock size={10} />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        let t2;
        if ($[1] !== scheduledFor) {
          t2 = scheduledFor ? formatScheduledFor(scheduledFor) : "Scheduled";
          $[1] = scheduledFor;
          $[2] = t2;
        } else {
          t2 = $[2];
        }
        let t3;
        if ($[3] !== t2) {
          t3 = <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">{t1}{t2}</span>;
          $[3] = t2;
          $[4] = t3;
        } else {
          t3 = $[4];
        }
        return t3;
      }
    case "POSTED":
      {
        let t1;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-500"><Check size={10} /> Live</span>;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "POSTING":
    case "QUEUED":
      {
        let t1;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Loader2 size={10} className="animate-spin" />;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        const t2 = status === "QUEUED" ? "Queued" : "Posting";
        let t3;
        if ($[7] !== t2) {
          t3 = <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/10 px-2 py-0.5 text-xs font-medium text-yellow-500">{t1}{t2}</span>;
          $[7] = t2;
          $[8] = t3;
        } else {
          t3 = $[8];
        }
        return t3;
      }
    case "FAILED":
      {
        const t1 = error ?? "";
        let t2;
        if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <AlertCircle size={10} />;
          $[9] = t2;
        } else {
          t2 = $[9];
        }
        let t3;
        if ($[10] !== t1) {
          t3 = <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-500" title={t1}>{t2} Failed</span>;
          $[10] = t1;
          $[11] = t3;
        } else {
          t3 = $[11];
        }
        return t3;
      }
    default:
      {
        return null;
      }
  }
}
function getCardBorderClass(canSelect: boolean, selected: boolean, status?: string): string {
  if (canSelect && selected) return 'border-primary bg-primary/5 shadow-sm';
  if (status === 'POSTED') return 'border-green-500/20 bg-background-secondary';
  if (status === 'FAILED') return 'border-red-500/20 bg-background-secondary';
  if (status === 'QUEUED' || status === 'POSTING') return 'border-yellow-500/20 bg-background-secondary';
  if (status === 'SCHEDULED') return 'border-blue-500/20 bg-background-secondary';
  if (canSelect) return 'border-ds-border-standard bg-background-secondary hover:border-primary/40 hover:shadow-sm';
  return 'border-ds-border-standard bg-background-secondary opacity-50';
}
const CrossPostPlatformCard = t0 => {
  const $ = _c(78);
  const {
    platform,
    compatible,
    connected,
    contentType,
    costTrons,
    selected,
    onToggle,
    crossPost,
    onRetry,
    onRefreshMetrics,
    onCancelScheduled,
    validationIssues,
    warning
  } = t0;
  const hasValidationIssues = validationIssues && validationIssues.length > 0;
  const canSelect = compatible && connected && !crossPost && !hasValidationIssues;
  const config = PLATFORM_CONFIG[platform];
  const Icon = config.icon;
  const isPosted = crossPost?.status === "POSTED";
  const isFailed = crossPost?.status === "FAILED";
  const isScheduled = crossPost?.status === "SCHEDULED";
  const hasMetrics = isPosted && crossPost.metrics.length > 0;
  let t1;
  if ($[0] !== crossPost || $[1] !== platform) {
    t1 = crossPost?.platformPostUrl ?? (crossPost?.platformPostId && platform !== "INSTAGRAM" ? getPlatformPostUrl(platform, crossPost.platformPostId) : null);
    $[0] = crossPost;
    $[1] = platform;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const postUrl = t1;
  let reason = null;
  if (!isPosted) {
    if (!connected) {
      reason = CROSSPOST_STATUS_TEXT.notConnected;
    } else {
      if (hasValidationIssues) {
        reason = validationIssues[0];
      } else {
        if (!compatible) {
          let t2;
          if ($[3] !== contentType || $[4] !== platform) {
            t2 = unsupportedReason(platform, contentType);
            $[3] = contentType;
            $[4] = platform;
            $[5] = t2;
          } else {
            t2 = $[5];
          }
          reason = t2;
        }
      }
    }
  }
  const t2 = crossPost?.status;
  let t3;
  if ($[6] !== canSelect || $[7] !== selected || $[8] !== t2) {
    t3 = getCardBorderClass(canSelect, selected, t2);
    $[6] = canSelect;
    $[7] = selected;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const borderClass = t3;
  const t4 = `group relative overflow-hidden rounded-xl border transition-all duration-200 ${borderClass} ${canSelect ? "cursor-pointer" : ""}`;
  let t5;
  if ($[10] !== canSelect || $[11] !== onToggle || $[12] !== platform) {
    t5 = () => canSelect && onToggle(platform);
    $[10] = canSelect;
    $[11] = onToggle;
    $[12] = platform;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = `flex size-10 shrink-0 items-center justify-center rounded-lg ${config.bg}`;
  let t7;
  if ($[14] !== Icon || $[15] !== config.color) {
    t7 = <Icon size={20} className={config.color} />;
    $[14] = Icon;
    $[15] = config.color;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== config.label) {
    t9 = <span className="text-sm font-semibold text-textPrimary">{config.label}</span>;
    $[20] = config.label;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== crossPost) {
    t10 = crossPost && <StatusIndicator status={crossPost.status} error={crossPost.error} scheduledFor={crossPost.scheduledFor} />;
    $[22] = crossPost;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div className="flex items-center gap-2">{t9}{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== reason) {
    t12 = reason && <span className="text-xs text-textSecondary">{reason}</span>;
    $[27] = reason;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== crossPost || $[30] !== reason || $[31] !== warning) {
    t13 = !reason && !crossPost && warning && <span className="block text-xs text-amber-500">{warning}</span>;
    $[29] = crossPost;
    $[30] = reason;
    $[31] = warning;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== crossPost || $[34] !== hasMetrics) {
    t14 = hasMetrics && <div className="mt-1 flex gap-3 text-xs text-textSecondary"><span>{formatNumber(crossPost.metrics[0].views)} views</span><span>{formatNumber(crossPost.metrics[0].likes)} likes</span><span>{formatNumber(crossPost.metrics[0].shares)} shares</span></div>;
    $[33] = crossPost;
    $[34] = hasMetrics;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== crossPost || $[37] !== isFailed) {
    t15 = isFailed && crossPost.error && <p className="mt-1 truncate text-xs text-red-400" title={crossPost.error}>{crossPost.error}</p>;
    $[36] = crossPost;
    $[37] = isFailed;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t14 || $[43] !== t15) {
    t16 = <div className="min-w-0 flex-1">{t11}{t12}{t13}{t14}{t15}</div>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== canSelect || $[46] !== costTrons) {
    t17 = costTrons > 0 && canSelect && <span className="rounded-md bg-background-primary px-2 py-0.5 text-xs text-textSecondary">{costTrons} T</span>;
    $[45] = canSelect;
    $[46] = costTrons;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== canSelect || $[49] !== selected) {
    t18 = canSelect && <div className={`flex size-5 items-center justify-center rounded-md border-2 transition-all ${selected ? "border-primary bg-primary text-white" : "border-ds-border-standard bg-background-primary group-hover:border-primary/40"}`}>{selected && <Check size={12} strokeWidth={3} />}</div>;
    $[48] = canSelect;
    $[49] = selected;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== config.label || $[52] !== isPosted || $[53] !== postUrl) {
    t19 = isPosted && postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" className="flex size-8 items-center justify-center rounded-lg text-textSecondary transition-colors hover:bg-background-primary hover:text-primary" onClick={_temp} title={`View on ${config.label}`}><ExternalLink size={14} /></a>;
    $[51] = config.label;
    $[52] = isPosted;
    $[53] = postUrl;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== t17 || $[56] !== t18 || $[57] !== t19) {
    t20 = <div className="flex shrink-0 items-center gap-2">{t17}{t18}{t19}</div>;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t16 || $[60] !== t20 || $[61] !== t8) {
    t21 = <div className="flex items-center gap-3 p-3">{t8}{t16}{t20}</div>;
    $[59] = t16;
    $[60] = t20;
    $[61] = t8;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== config.label || $[64] !== crossPost || $[65] !== isFailed || $[66] !== isPosted || $[67] !== isScheduled || $[68] !== onCancelScheduled || $[69] !== onRefreshMetrics || $[70] !== onRetry || $[71] !== postUrl) {
    t22 = (isPosted || isFailed || isScheduled) && <div className="flex items-center gap-1 border-t border-ds-border-standard px-3 py-1.5">{isPosted && onRefreshMetrics && <button className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-textSecondary transition-colors hover:bg-background-primary hover:text-primary" onClick={e_0 => {
        e_0.stopPropagation();
        onRefreshMetrics(crossPost.id);
      }}><RefreshCw size={12} />Refresh metrics</button>}{isFailed && onRetry && <button className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-textSecondary transition-colors hover:bg-background-primary hover:text-primary" onClick={e_1 => {
        e_1.stopPropagation();
        onRetry(crossPost.id);
      }}><RotateCcw size={12} />Retry</button>}{isScheduled && onCancelScheduled && <button className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-textSecondary transition-colors hover:bg-background-primary hover:text-red-500" onClick={e_2 => {
        e_2.stopPropagation();
        onCancelScheduled(crossPost.id);
      }}><Trash2 size={12} />Cancel</button>}{isPosted && postUrl && <a href={postUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-textSecondary transition-colors hover:bg-background-primary hover:text-primary" onClick={_temp2}><ChevronRight size={12} />View on {config.label}</a>}</div>;
    $[63] = config.label;
    $[64] = crossPost;
    $[65] = isFailed;
    $[66] = isPosted;
    $[67] = isScheduled;
    $[68] = onCancelScheduled;
    $[69] = onRefreshMetrics;
    $[70] = onRetry;
    $[71] = postUrl;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== t21 || $[74] !== t22 || $[75] !== t4 || $[76] !== t5) {
    t23 = <div className={t4} onClick={t5}>{t21}{t22}</div>;
    $[73] = t21;
    $[74] = t22;
    $[75] = t4;
    $[76] = t5;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  return t23;
};
export default CrossPostPlatformCard;
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_3) {
  return e_3.stopPropagation();
}